import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { toast } from '@/lib/toast'
import { createContact } from './api'
import { OrganisationCombobox, type OrganisationChoice } from './components/OrganisationCombobox'

/**
 * "Nieuw contact" (spec-47, organisation link spec-63): only the name is required. An existing
 * organisation is linked by id with the contact's role there; a new name is found or created by the API.
 */
export function NewContactDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (personId: string) => void
}) {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [organisation, setOrganisation] = useState<OrganisationChoice | null>(null)
  const [role, setRole] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const canSave = fullName.trim().length > 0 && !submitting

  async function save() {
    if (!canSave) return
    setSubmitting(true)
    try {
      const detail = await createContact({
        full_name: fullName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        // An existing organisation by id; a new name is found or created by the API.
        organisation_id: organisation?.kind === 'existing' ? organisation.id : undefined,
        organisation_name: organisation?.kind === 'new' ? organisation.name : undefined,
        role: role.trim() || undefined,
      })
      toast.success('Contact aangemaakt.')
      onOpenChange(false)
      onCreated(detail.person_id)
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Contact aanmaken mislukt.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nieuw contact</DialogTitle>
          <DialogDescription>
            Alleen de naam is verplicht; de rest vul je later aan op de detailpagina.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 flex flex-col gap-4">
          <label className="flex flex-col gap-2 text-sm font-medium">
            Naam *
            <Input
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              aria-label="Naam"
              autoFocus
            />
          </label>
          <label className="flex flex-col gap-2 text-sm font-medium">
            E-mail
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-label="E-mail"
            />
          </label>
          <label className="flex flex-col gap-2 text-sm font-medium">
            Telefoon
            <Input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              aria-label="Telefoon"
            />
          </label>
          <div className="flex flex-col gap-2 text-sm font-medium">
            <span aria-hidden>Organisatie</span>
            <OrganisationCombobox value={organisation} onChange={setOrganisation} />
          </div>
          <label className="flex flex-col gap-2 text-sm font-medium">
            Functie
            <Input
              value={role}
              onChange={(event) => setRole(event.target.value)}
              aria-label="Functie"
              placeholder="Bijv. Investment Director"
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Annuleren
          </Button>
          <Button onClick={() => void save()} disabled={!canSave}>
            {submitting ? 'Bezig…' : 'Aanmaken'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
